// SUN-174 mockup 2b: the small "Add product" / "Add vendor" window (and Edit for an existing link).
import { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { api } from '@/utils/api';
import { LINK_UNITS, apiError } from './vendorLinks';
import { Modal, useModalClose } from '@/components/ui/Modal';
import type { ButtonHTMLAttributes } from 'react';

export interface Pick { id: string; name: string; sku?: string | null }

interface Props {
  /** 'product' = opened from a vendor (pick a product); 'vendor' = opened from a raw material (pick a vendor). */
  pick: 'product' | 'vendor';
  /** The side that is already fixed: the vendor (pick='product') or the product (pick='vendor'). */
  fixed: Pick;
  /** Edit an existing link: nothing to pick, PATCH instead of POST. */
  edit?: { id: string; vendorItemCode?: string | null; rate?: number | string | null; unit?: string | null; leadTimeDays?: number | null; isPreferred: boolean; preferredName?: string | null };
  /** When the product is fixed (pick='vendor'), who is preferred today. */
  preferredName?: string | null;
  /** pick='vendor': does the fixed product already have a preferred vendor? undefined = still loading. */
  productHasPreferred?: boolean;
  onClose: () => void;
  onSaved: () => void;
}

async function searchProducts(q: string): Promise<Array<Pick & { raw: boolean }>> {
  // The server refuses an empty search (400), so send none; 25 is an allowed page size.
  const params: { search?: string; limit: number } = { limit: 25 };
  if (q.trim()) params.search = q.trim();
  const [rm, all] = await Promise.all([
    api.get('/products/raw-materials', { params }),
    api.get('/products', { params }),
  ]);
  const rawRows: any[] = rm?.data?.data?.rows ?? [];
  const others: any[] = all?.data?.data?.products ?? all?.data?.data ?? [];
  const seen = new Set<string>();
  const out: Array<Pick & { raw: boolean }> = [];
  for (const r of rawRows) if (!seen.has(r.id)) { seen.add(r.id); out.push({ id: r.id, name: r.name, sku: r.sku, raw: true }); }
  for (const p of Array.isArray(others) ? others : []) {
    if (seen.has(p.id)) continue;
    seen.add(p.id);
    out.push({ id: p.id, name: p.name, sku: p.sku, raw: p.productType === 'Raw Material' });
  }
  return out.sort((a, b) => Number(b.raw) - Number(a.raw));
}

async function searchVendors(q: string): Promise<Array<Pick & { raw: boolean }>> {
  const params: { search?: string; limit: number } = { limit: 25 };
  if (q.trim()) params.search = q.trim();
  const res = await api.get('/vendors', { params });
  const list: any[] = res.data?.data?.vendors ?? res.data?.data ?? [];
  return (Array.isArray(list) ? list : []).map((v) => ({ id: v.id, name: v.name, sku: v.gstin ?? null, raw: false }));
}

export function LinkWindow({ pick, fixed, edit, preferredName, productHasPreferred, onClose, onSaved }: Props) {
  const [query, setQuery] = useState('');
  const [choices, setChoices] = useState<Array<Pick & { raw: boolean }>>([]);
  const [chosen, setChosen] = useState<Pick | null>(null);
  const [code, setCode] = useState(edit?.vendorItemCode ?? '');
  const [rate, setRate] = useState(edit?.rate === null || edit?.rate === undefined ? '' : String(edit.rate));
  const [unit, setUnit] = useState(edit?.unit ?? '');
  const [lead, setLead] = useState(edit?.leadTimeDays === null || edit?.leadTimeDays === undefined ? '' : String(edit.leadTimeDays));
  const [preferred, setPreferred] = useState(edit?.isPreferred ?? false);
  const [today, setToday] = useState<string | null | undefined>(undefined);
  const [noPreferred, setNoPreferred] = useState<boolean | undefined>(undefined);
  const [touched, setTouched] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (edit) return;
    let live = true;
    setSearchError(null);
    (pick === 'product' ? searchProducts(query) : searchVendors(query)).then((c) => { if (live) setChoices(c); })
      .catch((e) => { if (live) { setChoices([]); setSearchError(apiError(e, 'Could not search. Try again.')); } });
    return () => { live = false; };
  }, [query, pick, edit]);

  // Product picked from the list: look up who is preferred for it today.
  useEffect(() => {
    const productId = edit ? fixed.id : chosen?.id;
    if (pick !== 'product' || !productId) return;
    let live = true;
    setToday(undefined); setNoPreferred(undefined);
    api.get(`/vendors/products/by-product/${productId}`)
      .then((r) => {
        if (!live) return;
        const links: any[] = r.data?.data?.links ?? r.data?.data ?? [];
        const name = links.find((l) => l.isPreferred)?.vendor?.name ?? null;
        setToday(name); setNoPreferred(!links.some((l) => l.isPreferred));
      })
      .catch(() => { if (live) setToday(null); });
    return () => { live = false; };
  }, [chosen, pick, edit, fixed.id]);

  // A product with no preferred vendor yet: this link should become it, so the box starts ticked.
  // Until the user touches the box we follow what the server says for the chosen product.
  const knownNoPreferred = edit ? undefined : pick === 'vendor' ? (productHasPreferred === undefined ? undefined : !productHasPreferred) : noPreferred;
  useEffect(() => { if (!touched && knownNoPreferred !== undefined) setPreferred(knownNoPreferred); }, [knownNoPreferred, touched]);

  const productName = pick === 'product' ? chosen?.name : fixed.name;
  const name = (n: string | null | undefined) => n ?? 'none';
  const todayText = pick === 'vendor' ? name(edit?.preferredName ?? preferredName) : !edit && !chosen ? '—' : today === undefined ? '…' : name(today);

  const save = async () => {
    const other = chosen;
    if (!edit && !other) { setError(pick === 'product' ? 'Pick a product first.' : 'Pick a vendor first.'); return; }
    const body: Record<string, unknown> = {
      vendorItemCode: code.trim() || undefined,
      rate: rate.trim() === '' ? undefined : Number(rate),
      unit: unit || undefined,
      leadTimeDays: lead.trim() === '' ? undefined : Number(lead),
      isPreferred: preferred,
    };
    if ((body.rate !== undefined && Number.isNaN(body.rate)) || (body.leadTimeDays !== undefined && Number.isNaN(body.leadTimeDays))) { setError('Rate and lead time must be numbers.'); return; }
    setBusy(true); setError(null);
    try {
      if (edit) await api.patch(`/vendors/products/${edit.id}`, body);
      else await api.post('/vendors/products', { ...body, vendorId: pick === 'product' ? fixed.id : other!.id, productId: pick === 'product' ? other!.id : fixed.id });
      onSaved();
    } catch (e: any) {
      const dup = e?.response?.status === 409 || e?.response?.data?.code === 'LINK_EXISTS';
      setError(dup ? `Already linked — ${apiError(e, 'this product is already linked to this vendor.')}` : apiError(e, 'Could not save the link.'));
    } finally { setBusy(false); }
  };

  const field = 'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm';
  const title = edit ? `Edit link · ${fixed.name}` : pick === 'product' ? `Add a product for ${fixed.name}` : `Add a vendor for ${fixed.name}`;
  return (
    <Modal open onClose={onClose} className="!max-w-md" ariaLabel={title}>
      <div>
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <RequestCloseButton aria-label="Close" className="text-gray-400 hover:text-gray-600"><X size={18} /></RequestCloseButton>
        </div>
        <div className="space-y-3 px-5 py-4">
          {!edit && (
            <div>
              <label className="block text-xs font-medium text-gray-600">{pick === 'product' ? 'Product (search raw materials first)' : 'Vendor'}
                <input aria-label={pick === 'product' ? 'Product' : 'Vendor'} value={chosen ? chosen.name : query}
                  onChange={(e) => { setChosen(null); setQuery(e.target.value); }} placeholder={pick === 'product' ? 'Search name or code' : 'Search vendor'} className={field} />
              </label>
              {searchError && <p role="alert" className="mt-1 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{searchError}</p>}
              {!chosen && (
                <ul role="listbox" aria-label="Matches" className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-gray-200 text-sm">
                  {choices.length === 0 && <li className="px-3 py-2 text-xs text-gray-400">No matches</li>}
                  {choices.map((c, i) => (
                    <li key={c.id}>
                      {pick === 'product' && c.raw && (i === 0 || !choices[i - 1].raw) && <p className="bg-gray-50 px-3 py-1 text-[11px] uppercase text-gray-500">Raw materials</p>}
                      {pick === 'product' && !c.raw && (i === 0 || choices[i - 1].raw) && <p className="bg-gray-50 px-3 py-1 text-[11px] uppercase text-gray-500">Other products</p>}
                      <button type="button" role="option" aria-selected={false} onClick={() => setChosen(c)} className="block w-full px-3 py-1.5 text-left hover:bg-gray-50">
                        {c.name}{c.sku ? <span className="ml-2 font-mono text-xs text-gray-500">{c.sku}</span> : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <label className="block text-xs font-medium text-gray-600">Vendor&apos;s code (optional)
            <input aria-label="Vendor's code" value={code} onChange={(e) => setCode(e.target.value)} className={field} />
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block text-xs font-medium text-gray-600">Rate ₹ (optional)
              <input aria-label="Rate" inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} className={field} />
            </label>
            <label className="block text-xs font-medium text-gray-600">Unit
              <select aria-label="Unit" value={unit} onChange={(e) => setUnit(e.target.value)} className={`${field} bg-white`}>
                <option value="">—</option>
                {unit && !LINK_UNITS.includes(unit) && <option value={unit}>{unit}</option>}
                {LINK_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </label>
            <label className="block text-xs font-medium text-gray-600">Lead time, days
              <input aria-label="Lead time, days" inputMode="numeric" value={lead} onChange={(e) => setLead(e.target.value)} className={field} />
            </label>
          </div>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input type="checkbox" aria-label="Make preferred" checked={preferred} onChange={(e) => { setTouched(true); setPreferred(e.target.checked); }} className="mt-1" />
            <span>★ Make this the preferred vendor for {productName ? <b>{productName}</b> : 'this product'} (today: {todayText})</span>
          </label>
          {error && <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <RequestCloseButton className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Cancel</RequestCloseButton>
          <button type="button" onClick={save} disabled={busy} className="flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            {busy && <Loader2 size={14} className="animate-spin" />}Save
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RequestCloseButton({ children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}
